import * as canvasLogics from './logics';

import {
  DEFAULT_CANVAS_SIZE,
  MAX_CANVAS_SIZE,
  MAX_SIDE_BAR_WIDTH,
  canvasResizingPhase,
  elementStatus,
  imageSharpenStatus,
  imprintOptionText,
  imprintOptionType,
  sidebarTabKeyEnum,
  userImageType,
} from '../utils/constants';
import { defaultDigitalTextColor, outlineThicknessOption } from '../text/constants';
import {
  getCurrentActiveCommonTemplate,
  getCurrentImprintAreaKey,
  getSidebarTabKey,
  getSloganMandatoryCheck,
  getStudioItemType,
} from '../main/selectors';
import { getSelectedColorBomOptions, replaceFullPreviewUrlPlaceholder } from '../preview/selectors';
import { promoStudioUITypeValue, studioItemType as studioItemTypeEnum } from '../main/constants';

import { createSelector } from 'reselect';
import { getImprintOptionType } from '../imprintarea/selectors';
import { getPromoStudioUIType } from '../lite/selectors';
import { getTempRecentBackground } from '../template/selectors';
import imprintarea from '../imprintarea';

const defaultDraggingArea = {
  x: 0,
  y: 0,
  width: DEFAULT_CANVAS_SIZE,
  height: DEFAULT_CANVAS_SIZE,
  isCalendar: false,
  sideWidth: 0,
  foldLeft: 0,
  foldRight: 0,
  designCenter: 0,
};

export const getCanvasDict = createSelector(
  (state) => state.canvasDict,
  (canvasDict) => canvasDict
);

export const getCanvasNumber = createSelector(
  (state) => state.canvasDict,
  (canvasDict) => Object.keys(canvasDict).length
);

export const getImageDict = createSelector(
  (state) => state.imageDict,
  (imageDict) => imageDict
);

export const getCanvasImageList = createSelector(
  (state, formatkey) => formatkey || getCurrentImprintAreaKey(state),
  (state) => state.imageDict,
  (currentCanvasKey, imageDict) => {
    let imageList = imageDict[currentCanvasKey] || [];
    return imageList;
  }
);

export const getCanvasImageDataDict = createSelector(
  imprintarea.selectors.getSelectedImprintAreaKeys,
  getStudioItemType,
  (state) => state.imageDict,
  (selectedImprintAreaKeys, studioItemType, imageDict) => {
    let imageDataDict = {};
    for (let selectedImprintAreaKey of selectedImprintAreaKeys) {
      let imageDataList = [];
      let canvasImageList = imageDict[selectedImprintAreaKey];
      if (canvasImageList !== undefined) {
        for (let canvasImage of canvasImageList) {
          let imageData = Object.assign({}, canvasImage.data);
          imageData.studioItemType = studioItemType;

          imageDataList.push(imageData);
        }
        imageDataDict[selectedImprintAreaKey] = imageDataList;
      }
    }
    return imageDataDict;
  }
);

export const getSelectedSpecialImageData = createSelector(
  getCanvasImageDataDict,
  (state, imprintAreaKey) => imprintAreaKey || getCurrentImprintAreaKey(state),
  (canvasImageDataDict, imprintAreaKey) => {
    let imageDataList = canvasImageDataDict[imprintAreaKey] || [];
    const selectedSpecialImageData = imageDataList.find(
      (imageData) =>
        imageData.type === userImageType.Verse ||
        imageData.type === userImageType.Sentiment ||
        (imageData.type === userImageType.Slogan && imageData.isOutside)
    );
    return selectedSpecialImageData;
  }
);

export const getCanvasImageDataDictForAddCart = createSelector(
  imprintarea.selectors.getSelectedImprintAreaKeys,
  getStudioItemType,
  (state) => state.imageDict,
  (selectedImprintAreaKeys, studioItemType, imageDict) => {
    let imageDataDict = {};
    for (let selectedImprintAreaKey of selectedImprintAreaKeys) {
      let imageDataList = [];
      let canvasImageList = imageDict[selectedImprintAreaKey];
      if (canvasImageList !== undefined) {
        for (let canvasImage of canvasImageList) {
          let imageData = Object.assign({}, canvasImage.data);

          imageData = canvasLogics.restoreCropRect(imageData, canvasImage.ui);

          imageData.studioItemType = studioItemType;
          imageDataList.push(imageData);
        }
        imageDataDict[selectedImprintAreaKey] = imageDataList;
      }
    }
    return imageDataDict;
  }
);

// export const getCanvasTextList = createSelector(
//   getCurrentImprintAreaKey,
//   (state) => state.textDict,
//   (currentCanvasKey, textDict) => {
//     let textList = textDict[currentCanvasKey] || [];
//     return textList;
//   }
// );

export const hasTextInput = createSelector(
  imprintarea.selectors.getSelectedImprintAreaKeys,
  (state) => state.textDict,
  (selectedImprintAreaKeys, textDict) => {
    let hasTextInput = false;
    for (let selectedImprintAreaKey of selectedImprintAreaKeys) {
      let textElementList = textDict[selectedImprintAreaKey];
      if (textElementList !== undefined) {
        hasTextInput = textElementList.some((textElement) => textElement.data.imprintText !== '');
        if (hasTextInput) break;
      }
    }
    return hasTextInput;
  }
);

export const checkSingleSelectedImage = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.imageDict,
  (currentCanvasKey, imageDict) => {
    if (imageDict[currentCanvasKey]) {
      const singleSelectedImage = imageDict[currentCanvasKey].find(
        (image) => image.ui.status === elementStatus.SingleSelected
      );
      return singleSelectedImage ? true : false;
    }

    return false;
  }
);

export const hasSingleSelectedText = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.textDict,
  (currentCanvasKey, textDict) => {
    if (textDict[currentCanvasKey]) {
      const singleSelectedText = textDict[currentCanvasKey].find(
        (text) => text.ui.status === elementStatus.SingleSelected
      );
      return singleSelectedText ? true : false;
    }

    return false;
  }
);

export const hasBatchSelectedText = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.textDict,
  (currentCanvasKey, textDict) => {
    if (textDict[currentCanvasKey]) {
      const batchSelectedText = textDict[currentCanvasKey].find(
        (text) => text.ui.status === elementStatus.BatchSelected
      );
      return batchSelectedText ? true : false;
    }

    return false;
  }
);

export const getBatchSelectedTextList = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.textDict,
  (currentCanvasKey, textDict) => {
    if (textDict[currentCanvasKey]) {
      const batchSelectedTextElement = textDict[currentCanvasKey].find(
        (text) => text.ui.status === elementStatus.BatchSelected
      );
      const tempSelectedTextElement = textDict[currentCanvasKey].filter(
        (text) => text.ui.status === elementStatus.TempSelected
      );
      if (batchSelectedTextElement || tempSelectedTextElement.length > 1) {
        return textDict[currentCanvasKey].filter(
          (text) => text.ui.status === elementStatus.BatchSelected || text.ui.status === elementStatus.TempSelected
        );
      }
    }
    return [];
  }
);

export const getCurrentCanvasData = createSelector(
  getStudioItemType,
  (state, canvasKey) => canvasKey || getCurrentImprintAreaKey(state),
  (state) => state.canvasDict,
  (state) => state.imageDict,
  (state) => state.textDict,
  (studioItemType, currentCanvasKey, canvasDict, imageDict, textDict) => {
    let canvasData = Object.assign({}, canvasDict[currentCanvasKey]);
    canvasData.imageList = imageDict[currentCanvasKey] ? imageDict[currentCanvasKey] : [];
    canvasData.textList = textDict[currentCanvasKey] ? textDict[currentCanvasKey] : [];
    if (studioItemType === studioItemTypeEnum.nonDigital) {
      canvasData.fullWidth = DEFAULT_CANVAS_SIZE;
      canvasData.fullHeight = DEFAULT_CANVAS_SIZE;
      canvasData.bleedTop = 0;
      canvasData.bleedRight = 0;
      canvasData.bleedBottom = 0;
      canvasData.bleedLeft = 0;
      canvasData.marginTop = 0;
      canvasData.marginRight = 0;
      canvasData.marginBottom = 0;
      canvasData.marginLeft = 0;
    }
    return canvasData;
  }
);

export const getCanvasSizeInfo = createSelector(
  (state) => state.canvasDict,
  (state, canvasKey) => canvasKey || getCurrentImprintAreaKey(state),
  (canvasDict, canvasKey) => {
    if (canvasKey) {
      const canvas = canvasDict[canvasKey];
      const canvasWidth = canvas.fullWidth / canvas.previewResolution;
      const canvasHeight = canvas.fullHeight / canvas.previewResolution;

      return { canvasWidth, canvasHeight };
    }

    return null;
  }
);

export const getAllowText = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.canvasDict,
  (currentCanvasKey, canvasDict) => {
    let canvasData = Object.assign({}, canvasDict[currentCanvasKey]);
    if (canvasData && canvasData.allowText) return true;
    return false;
  }
);

export const getAllowLogo = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.canvasDict,
  (currentCanvasKey, canvasDict) => {
    let canvasData = Object.assign({}, canvasDict[currentCanvasKey]);
    if (canvasData && canvasData.allowLogo) return true;
    return false;
  }
);

export const getCanvasDraggingArea = createSelector(
  getStudioItemType,
  (state, canvasKey) => canvasKey || getCurrentImprintAreaKey(state),
  (state) => state.canvasDict,
  (studioItemType, canvasKey, canvasDict) => {
    const canvas = canvasDict[canvasKey];
    if (
      (studioItemType === studioItemTypeEnum.digital || studioItemType === studioItemTypeEnum.screenPrintedDigital) &&
      canvas
    ) {
      return {
        x: canvas.extraMarginX + canvas.bleedLeft + canvas.marginLeft,
        y: canvas.extraMarginY + canvas.bleedTop + canvas.marginTop,
        width: canvas.fullWidth - canvas.bleedLeft - canvas.marginLeft - canvas.bleedRight - canvas.marginRight,
        height: canvas.fullHeight - canvas.bleedTop - canvas.marginTop - canvas.bleedBottom - canvas.marginBottom,
        isCalendar: canvas.isCalendar,
        sideWidth: canvas.sideWidth,
        foldLeft: canvas.foldLeft,
        foldRight: canvas.foldRight,
        designCenter: canvas.designCenter,
      };
    } else {
      return defaultDraggingArea;
    }
  }
);

export const getTempArt = createSelector(
  (state, imprintAreaKey) => imprintAreaKey || getCurrentImprintAreaKey(state),
  (state) => state.imageDict,
  (currentCanvasKey, imageDict) => {
    const imageList = imageDict[currentCanvasKey] ? imageDict[currentCanvasKey] : [];
    return imageList.find((image) => image.ui.status === elementStatus.TempSelected);
  }
);

export const getTempText = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.textDict,
  (currentCanvasKey, textDict) => {
    const imageList = textDict[currentCanvasKey] ? textDict[currentCanvasKey] : [];
    return imageList.find((image) => image.ui.status === elementStatus.TempSelected);
  }
);
export const getTempElement = createSelector(
  getTempRecentBackground,
  getCurrentActiveCommonTemplate,
  getTempArt,
  getTempText,
  (tempBackground, selectedSearchBackground, tempArt, tempText) => {
    return tempBackground || selectedSearchBackground || tempArt || tempText;
  }
);
export const getSelectedImage = createSelector(
  getStudioItemType,
  getCurrentImprintAreaKey,
  getSidebarTabKey,
  (state) => state.imageDict,
  (studioItemType, currentCanvasKey, sidebarTabKey, imageDict) => {
    const imageList = imageDict[currentCanvasKey] ? imageDict[currentCanvasKey] : [];
    if (studioItemType === studioItemTypeEnum.digital || studioItemType === studioItemTypeEnum.screenPrintedDigital)
      return imageList.find((image) => image.ui.status === elementStatus.SingleSelected);
    else {
      if (sidebarTabKey === sidebarTabKeyEnum.Logo)
        return imageList.find(
          (image) =>
            image.ui.status === elementStatus.SingleSelected &&
            (typeof image.data.isOutside === 'undefined' || !image.data.isOutside) &&
            image.data.type !== userImageType.Verse &&
            image.data.type !== userImageType.Sentiment
        );
      else if (sidebarTabKey === sidebarTabKeyEnum.OutsideSlogan)
        return imageList.find(
          (image) =>
            image.ui.status === elementStatus.SingleSelected &&
            typeof image.data.isOutside !== 'undefined' &&
            image.data.isOutside
        );
      else if (sidebarTabKey === sidebarTabKeyEnum.Verse)
        return imageList.find(
          (image) => image.ui.status === elementStatus.SingleSelected && image.data.type === userImageType.Verse
        );
      else if (sidebarTabKey === sidebarTabKeyEnum.Sentiment)
        return imageList.find(
          (image) => image.ui.status === elementStatus.SingleSelected && image.data.type === userImageType.Sentiment
        );
    }
  }
);

export const getLogoSharpenStatus = createSelector(getSelectedImage, (selectedLogo) => {
  return selectedLogo ? selectedLogo.data.sharpenStatus : imageSharpenStatus.NotAvailable;
});

export const getLogoSharpenMessageStatus = createSelector(getSelectedImage, (selectedLogo) => {
  return selectedLogo ? selectedLogo.ui.sharpenMessageStatus : { show: false };
});

export const getStudioScaleByKey = createSelector(
  getStudioItemType,
  (state, imprintAreaKey) => state.canvasDict[imprintAreaKey],
  (studioItemType, canvas) => {
    return studioItemType === studioItemTypeEnum.digital || studioItemType === studioItemTypeEnum.screenPrintedDigital
      ? canvas.bigResOverScreenRes
      : 1;
  }
);
export const getCurrentStudioScale = createSelector(
  getCurrentImprintAreaKey,
  (state) => state,
  (currentCanvasKey, state) => {
    return getStudioScaleByKey(state, currentCanvasKey);
  }
);

export const getCanvasScale = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.ui.canvasResize,
  (imprintAreaKey, canvasResize) => {
    if (canvasResize[imprintAreaKey]) {
      return canvasResize[imprintAreaKey].scale;
    }
    return 1;
  }
);
export const getSideBarWidth = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.ui.canvasResize,
  (imprintAreaKey, canvasResize) => {
    if (canvasResize[imprintAreaKey]) {
      return canvasResize[imprintAreaKey].sideBarWidth;
    }
    return MAX_SIDE_BAR_WIDTH;
  }
);
export const getCanvasWidth = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.ui.canvasResize,
  (imprintAreaKey, canvasResize) => {
    if (canvasResize[imprintAreaKey]) {
      return canvasResize[imprintAreaKey].canvasWidth;
    }
    return 0;
  }
);
export const getCanvasResizeInfo = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.ui.canvasResize,
  (imprintAreaKey, canvasResize) => {
    if (canvasResize[imprintAreaKey]) {
      return canvasResize[imprintAreaKey];
    }
    return {};
  }
);
export const getCanvasResizingPhase = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.ui.canvasResize,
  (imprintAreaKey, canvasResize) => {
    if (canvasResize[imprintAreaKey]) {
      return canvasResize[imprintAreaKey].resizingPhase;
    }
    return canvasResizingPhase.OneRowAndNoResizing;
  }
);
export const getItemStyleOriginSize = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.ui.canvasResize,
  (imprintAreaKey, canvasResize) => {
    if (canvasResize[imprintAreaKey]) {
      if (canvasResize[imprintAreaKey].itemStyleOriginSize) {
        return canvasResize[imprintAreaKey].itemStyleOriginSize;
      }
    }
    return { width: 0, height: 0 };
  }
);

export const getEditPanelHeight = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.ui.canvasResize,
  (imprintAreaKey, canvasResize) => {
    if (canvasResize[imprintAreaKey]) {
      if (canvasResize[imprintAreaKey].editPanelHeight) {
        return canvasResize[imprintAreaKey].editPanelHeight;
      }
    }
    return 0;
  }
);

export const getFixedContainerChangeFlag = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.ui.canvasResize,
  (imprintAreaKey, canvasResize) => {
    if (canvasResize[imprintAreaKey]) {
      if (canvasResize[imprintAreaKey].fixedContainerChangeFlag) {
        return canvasResize[imprintAreaKey].fixedContainerChangeFlag;
      }
    }
    return null;
  }
);

export const getCurrentSuperUserTextDataDict = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.superUserTextDict,
  (imprintAreaKey, superUserTextDict) => {
    if (!superUserTextDict[imprintAreaKey]) {
      return ['', '', ''];
    } else {
      return superUserTextDict[imprintAreaKey];
    }
  }
);

export const getTempLogo = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.imageDict,
  (currentCanvasKey, imageDict) => {
    const imageList = imageDict[currentCanvasKey] ? imageDict[currentCanvasKey] : [];
    return imageList.find(
      (image) => image.data.type === userImageType.UploadLogo && image.ui.status === elementStatus.TempSelected
    );
  }
);

// only used in non-digital/screenprinted panels that need temp selected logo
export const getCurrentLogo = createSelector(
  getStudioItemType,
  getCurrentImprintAreaKey,
  (state) => state.imageDict,
  (studioItemType, currentCanvasKey, imageDict) => {
    const imageList = imageDict[currentCanvasKey] ? imageDict[currentCanvasKey] : [];
    const currentLogo = imageList.find(
      (image) =>
        image.data.type === userImageType.UploadLogo &&
        (image.ui.status === elementStatus.TempSelected || image.ui.status === elementStatus.SingleSelected)
    );

    if (currentLogo && currentLogo.data) {
      const logo = { data: {}, ui: {} };
      Object.assign(logo.data, currentLogo.data);
      Object.assign(logo.ui, currentLogo.ui);
      if (studioItemType === studioItemTypeEnum.nonDigital) {
        // non-digital has no width/height, set their values from finalCropRect
        logo.data.width = logo.data.finalCropRect.width;
        logo.data.height = logo.data.finalCropRect.height;
      }
      return logo;
    }
  }
);

export const getCurrentScaleFactor = createSelector(
  getStudioItemType,
  (state, imprintAreaKey) => imprintAreaKey || getCurrentImprintAreaKey(state),
  (state) => state.canvasDict,
  (state) => state,
  (studioItemType, imprintAreaKey, canvasDict, state) => {
    let imprintFormat = imprintarea.selectors.getCurrentImprintFormat(state, imprintAreaKey);
    if (studioItemType === studioItemTypeEnum.digital || studioItemType === studioItemTypeEnum.screenPrintedDigital) {
      const canvas = canvasDict[imprintAreaKey];
      const scaleFactor = state.specs.nextGenUI
        ? MAX_CANVAS_SIZE / canvas.fullWidth // ignore maxDisplayWidth if nextgen ui
        : (imprintFormat.maxDisplayWidth === 0 ? MAX_CANVAS_SIZE : imprintFormat.maxDisplayWidth) / canvas.fullWidth;
      return scaleFactor;
    } else return 1;
  }
);

export const getImprintElementExistStatus = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.imageDict,
  (state) => state.textDict,
  (currentCanvasKey, imageDict, textDict) => {
    const imageList = imageDict[currentCanvasKey] ? imageDict[currentCanvasKey] : [];
    const textList = textDict[currentCanvasKey] ? textDict[currentCanvasKey] : [];

    const hasLogoArt = imageList.find(
      (image) =>
        (typeof image.data.isOutside === 'undefined' || !image.data.isOutside) &&
        image.data.type !== userImageType.Verse &&
        image.data.type !== userImageType.Sentiment
    )
      ? true
      : false;

    const hasOutsideSlogan = imageList.find(
      (image) => typeof image.data.isOutside !== 'undefined' && image.data.isOutside
    )
      ? true
      : false;

    const hasVerse = imageList.find((image) => image.data.type === userImageType.Verse) ? true : false;

    const hasSentiment = imageList.find((image) => image.data.type === userImageType.Sentiment) ? true : false;

    const hasText = textList.find((text) => text.data.imprintText.length > 0) ? true : false;

    return { hasLogoArt, hasOutsideSlogan, hasVerse, hasSentiment, hasText };
  }
);

export const getMissingNonDigitalOptionType = createSelector(
  (state, currentImprintAreaKey) => currentImprintAreaKey || getCurrentImprintAreaKey(state),
  (state, currentImprintAreaKey) => getImprintOptionType(state, currentImprintAreaKey),
  (state) => state.imageDict,
  (state) => state.textDict,
  (state) => state.imprintAreaDict,
  getSloganMandatoryCheck,
  getPromoStudioUIType,
  imprintarea.selectors.getOpitonalText,
  (
    currentImprintAreaKey,
    currentOptionType,
    imageDict,
    textDict,
    imprintAreaDict,
    sloganMandatoryCheck,
    uiType,
    optionalText
  ) => {
    if (currentImprintAreaKey) {
      let hasText = true;
      let textFlag = '';
      if (currentOptionType === imprintOptionType.TextOnly || currentOptionType === imprintOptionType.TextLogo) {
        //when imprintOptionType is TextOnly. Text side bar may not be showed
        if (textDict[currentImprintAreaKey] && textDict[currentImprintAreaKey].length !== 0)
          hasText = textDict[currentImprintAreaKey].some((item) => item.data.imprintText.trim() !== '');
      }
      //outside slogan, verse, sentiment not belong to logo. but inner slogan belong to art when imprintOptionType is logo/art
      const hasLogo =
        imageDict[currentImprintAreaKey] &&
        imageDict[currentImprintAreaKey].some(
          (item) =>
            item.data.type !== userImageType.Verse &&
            item.data.type !== userImageType.Sentiment &&
            !(item.data.type === userImageType.Slogan && item.data.isOutside)
        );

      //when add (outside slogan, verse, sentiment) of mandatory, logo/art or text is not mandatory
      const hasAllowedAddOnlyOneLogo =
        imageDict[currentImprintAreaKey] &&
        imageDict[currentImprintAreaKey].some(
          (item) =>
            item.data.type === userImageType.Verse ||
            item.data.type === userImageType.Sentiment ||
            (item.data.type === userImageType.Slogan && item.data.isOutside)
        );

      const noSelectedNoMandatorySlogan =
        imprintAreaDict[currentImprintAreaKey].sloganAvailable &&
        !sloganMandatoryCheck &&
        imageDict[currentImprintAreaKey] &&
        !imageDict[currentImprintAreaKey].some((item) => item.data.type === userImageType.Slogan);
      const validateText = hasText || (uiType === promoStudioUITypeValue.LiteUI && optionalText);
      switch (currentOptionType) {
        case imprintOptionType.TextOnly:
          if (!validateText) textFlag = imprintOptionText.TextOnly;
          break;
        case imprintOptionType.LogoOnly:
          if (!hasLogo) textFlag = imprintOptionText.LogoOnly;
          break;
        case imprintOptionType.TextLogo:
          if (!validateText && !hasLogo) textFlag = imprintOptionText.TextLogo;
          else if (!hasLogo) textFlag = imprintOptionText.LogoOnly;
          else if (!validateText) textFlag = imprintOptionText.TextOnly;
          break;
        default:
          break;
      }
      let missingOptionTypeObj = {};
      if (textFlag !== '') {
        missingOptionTypeObj = {
          askNeedImprintOption: hasAllowedAddOnlyOneLogo,
          optionText: textFlag,
          imprintAreaDescription: imprintAreaDict[currentImprintAreaKey].description,
          imprintAreaKey: currentImprintAreaKey,
        };
      } else if (noSelectedNoMandatorySlogan && currentOptionType === imprintOptionType.TextOnly) {
        //If the user does not add an mandate slogan, web need show warning message to ask if it is continue
        missingOptionTypeObj = {
          askNeedImprintOption: true,
          optionText: imprintOptionText.Slogan,
          imprintAreaDescription: imprintAreaDict[currentImprintAreaKey].description,
          imprintAreaKey: currentImprintAreaKey,
        };
      }
      return missingOptionTypeObj;
    }
  }
);

export const getIfUserNeedImprintOption = createSelector(
  (state) => state.ui,
  (ui) => ui.askIfUserNeedImprintOption
);

export const getUndoRedoStack = createSelector(
  (state) => state.undoRedoStack,
  (undoRedoStack) => undoRedoStack
);

export const getCanvasPreview = createSelector(
  (state, imprintAreaKey) => imprintAreaKey || getCurrentImprintAreaKey(state),
  (state) => state.ui.canvasPreview,
  getSelectedColorBomOptions,
  (imprintAreaKey, canvasPreview, bomOptions) => {
    const preview = canvasPreview[imprintAreaKey]
      ? decodeURIComponent(replaceFullPreviewUrlPlaceholder(canvasPreview[imprintAreaKey], bomOptions))
      : '';
    return preview;
  }
);
